import { useState } from "react";
import { Plus, Phone, HelpCircle, ArrowUpRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F2EDE2";
const PAPER = "#FFFEFB";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FAQS = [{
  q: "How quickly can you come out to fix my garage door?",
  a: "Most Sydney suburbs within 60–90 minutes during business hours, with after-hours emergency callouts available 24/7. Call us with your suburb and the symptoms — we'll quote a live ETA on the spot.",
  related: {
    label: "Same-day service",
    href: "tel:0485000933"
  }
}, {
  q: "Do you charge a callout fee?",
  a: "No — never. We don't charge to come look at your door or give you a fixed-price quote. If you don't approve the work, you walk away at zero cost. No travel surcharge, no hidden fees.",
  related: null
}, {
  q: "How much does a typical garage door repair cost?",
  a: "It depends on what's actually failed — a snapped spring, burnt-out motor, and frayed cable are very different jobs. What's locked in regardless: a fixed price quoted on-site in writing, before any tool comes out. GST included, no hourly creep, no surprises.",
  related: {
    label: "How our pricing works",
    href: "#pricing"
  }
}, {
  q: "What brands and door types do you service?",
  a: "Every major brand — B&D, Merlin, Chamberlain, ATA, Steel-Line, Gliderol — across sectional, roller, tilt, and panel-lift doors. Residential, strata, and small commercial all covered. One number for any door type.",
  related: {
    label: "See all door brands",
    href: "/garage-door-services-sydney/"
  }
}, {
  q: "Do you offer a warranty on repairs?",
  a: "Every repair comes with a 12-month workmanship warranty in writing (not just email). Replacement parts also covered by their manufacturer's warranty — we hand all the paperwork to you on completion. If our work fails inside the window, we come back free.",
  related: null
}, {
  q: "What if my door is too far gone to repair?",
  a: "If it's beyond economic repair, we'll tell you straight — no upsell scripts, no pressure. You'll get a fixed-price quote for a like-for-like replacement or upgrade, and you decide what makes sense for your home and budget.",
  related: {
    label: "New door installation",
    href: "/garage-door-installation-sydney/"
  }
}, {
  q: "Which Sydney suburbs do you cover?",
  a: "154+ suburbs across Greater Sydney — Eastern Suburbs, Inner West, Parramatta, North Shore, Hills District, Sutherland Shire, Northern Beaches, Western Sydney, plus the Central Coast and Wollongong. If you're not sure, call us with your suburb.",
  related: {
    label: "Check service areas",
    href: "/#service-areas"
  }
}];
export default function RepairFaq() {
  const [openValue, setOpenValue] = useState<string>("item-0");
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="repair-faq-heading">
      {/* FAQ Schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map(f => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a
          }
        }))
      })
    }} />

      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Decorative blob */}
      <div aria-hidden className="absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full pointer-events-none opacity-[0.05]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* === Top strip === */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD_DARK}66`,
            backgroundColor: `${GOLD}1A`
          }}>
              <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              Frequently Asked
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${NAVY}26`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            {FAQS.length} Questions · Real Answers
          </div>
        </div>

        {/* === SPLIT LAYOUT === */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* === LEFT — Sticky header === */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            {/* H2 */}
            <h2 id="repair-faq-heading" className="font-extrabold" style={{
            fontFamily: FONT_HEAD,
            color: NAVY,
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.03em"
          }}>
              The questions
              <br />
              we hear{" "}
              <span style={{
              color: GOLD_DARK
            }}>most often.</span>
            </h2>

            {/* Subtitle */}
            <p className="mt-6 text-[16px] leading-relaxed max-w-md" style={{
            color: INK_MUTED
          }}>
              Straight answers to the things Sydney homeowners actually ask
              before booking a repair. If your question isn&rsquo;t here, the
              quickest way to get an answer is to call.
            </p>

            {/* Inline CTA card */}
            <div className="mt-8 relative rounded-2xl border overflow-hidden p-5" style={{
            backgroundColor: PAPER,
            borderColor: `${NAVY}14`,
            boxShadow: `0 8px 28px -16px ${NAVY}26`
          }}>
              {/* Top gold accent */}
              <div aria-hidden className="absolute top-0 left-5 right-5 h-px" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}40 100%)`
            }} />

              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl" style={{
                backgroundColor: NAVY,
                color: GOLD
              }}>
                  <MessageCircle className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-1" style={{
                  color: GOLD_DARK
                }}>
                    Question not listed?
                  </div>
                  <p className="text-[14px] leading-snug font-medium mb-3" style={{
                  color: INK
                }}>
                    Tell us what your door is doing &mdash; we&rsquo;ve probably
                    diagnosed it on the phone before.
                  </p>
                  <a href="tel:0485000933" className="group inline-flex items-center gap-2 text-[13px] font-bold transition-colors" style={{
                  color: NAVY,
                  fontFamily: FONT_BODY
                }} onMouseEnter={e => e.currentTarget.style.color = GOLD_DARK} onMouseLeave={e => e.currentTarget.style.color = NAVY}>
                    <Phone className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Call 0485 000 933
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* === RIGHT — Accordion === */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={setOpenValue} className="w-full">
              {FAQS.map((faq, idx) => {
              const value = `item-${idx}`;
              const isOpen = openValue === value;
              const num = String(idx + 1).padStart(2, "0");
              return <AccordionItem key={value} value={value} className="border-0 mb-3">
                    <div className="rounded-2xl border overflow-hidden transition-all duration-300" style={{
                  backgroundColor: isOpen ? PAPER : "transparent",
                  borderColor: isOpen ? `${GOLD_DARK}40` : `${NAVY}14`,
                  boxShadow: isOpen ? `0 20px 50px -20px ${NAVY}26` : "none"
                }}>
                      <AccordionTrigger className="group w-full px-5 md:px-6 py-5 md:py-6 hover:no-underline [&[data-state=open]>div>span.chev]:rotate-45 [&[data-state=open]>div>span.chev]:bg-[#F5A623] [&[data-state=open]>div>span.chev>svg]:text-white [&>svg]:hidden">
                        <div className="flex items-center gap-4 md:gap-5 w-full text-left">
                          {/* Number */}
                          <span className="flex-shrink-0 font-extrabold leading-none transition-colors duration-300" style={{
                        fontFamily: FONT_HEAD,
                        color: isOpen ? GOLD_DARK : `${GOLD_DARK}40`,
                        fontSize: "1.5rem",
                        letterSpacing: "-0.02em",
                        fontVariantNumeric: "tabular-nums",
                        minWidth: "2.2ch"
                      }}>
                            {num}
                          </span>

                          {/* Question */}
                          <span className="flex-1 min-w-0 font-bold text-left transition-colors duration-200" style={{
                        fontFamily: FONT_HEAD,
                        color: isOpen ? NAVY : INK,
                        fontSize: "clamp(0.95rem, 1.3vw, 1.0625rem)",
                        lineHeight: 1.35,
                        letterSpacing: "-0.012em"
                      }}>
                            {faq.q}
                          </span>

                          {/* Plus icon */}
                          <span className="chev flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-300" style={{
                        borderColor: isOpen ? GOLD_DARK : `${NAVY}26`,
                        backgroundColor: isOpen ? GOLD : "transparent"
                      }}>
                            <Plus className="h-4 w-4 transition-colors" strokeWidth={2.5} style={{
                          color: isOpen ? "white" : NAVY as React.CSSProperties["color"]
                        }} />
                          </span>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-5 md:px-6 pb-6">
                        <div className="ml-0 md:ml-[3.7rem] pt-1">
                          {/* Gold rule */}
                          <div aria-hidden className="h-px mb-5 max-w-md" style={{
                        background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}26 100%)`
                      }} />

                          {/* Answer */}
                          <p className="text-[14.5px] md:text-[15px] leading-[1.7]" style={{
                        color: INK,
                        fontFamily: FONT_BODY
                      }}>
                            {faq.a}
                          </p>

                          {/* Related link */}
                          {faq.related && <a href={faq.related.href} className="group mt-4 inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] uppercase transition-colors" style={{
                        color: GOLD_DARK,
                        fontFamily: FONT_BODY
                      }} onMouseEnter={e => e.currentTarget.style.color = NAVY} onMouseLeave={e => e.currentTarget.style.color = GOLD_DARK}>
                              <span className="inline-block w-4 h-px" style={{
                          backgroundColor: "currentColor"
                        }} />
                              {faq.related.label}
                              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                            </a>}
                        </div>
                      </AccordionContent>
                    </div>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}